import { itemMatchesSourceKey } from './sourceIcons'

export const FILTERED_DISABLED_HINT = "Dragging and editing are disabled because this item is marked to be ignored.";
export const FILTERED_DELETE_HINT = "Filtered items will be deleted upon time entry creation.";
export const FILTER_ACTION_TOOLTIP = "Ignore this item if it is not work-related so it won't appear in your time entries.";
export const RECOVER_ACTION_TOOLTIP = "This item is ignored because it appears to be non-work-related. Click to restore it.";
export const RECOVER_SUB_ITEMS_ACTION_TOOLTIP = "Some sub-items are ignored because they appear to be non-work-related. Click to restore them.";
export const NETDOCUMENTS_NOISE_HINT =
  "This event is used as a supporting signal and does not include a duration. MIRA correlates related activities to determine actual work time, which is reflected in the associated activity entry.";

export const isNoiseItem = (item) => item?.is_noise === true || item?.is_noise === "true";

export const isNetDocumentsItem = (item) => {
  if (!item) return false;
  if (itemMatchesSourceKey(item, 'NetDocuments')) return true;
  if (item.non_svc_source === 'NetDocuments') return true;
  return Array.isArray(item.apps) && item.apps.includes('NetDocuments');
};

export const isNetDocumentsNoiseItem = (item) =>
  isNetDocumentsItem(item) && (isNoiseItem(item) || isNoiseEntry(item));

export const getItemSlots = (item) => (Array.isArray(item?.slots) ? item.slots : []);

export const getNoiseSlotCount = (item) => {
  if (!item) return 0;
  const slots = getItemSlots(item);
  if (slots.length) return slots.filter(isNoiseItem).length;
  return isNoiseItem(item) ? 1 : 0;
};

export const hasNoiseSlot = (item) => getNoiseSlotCount(item) > 0;

export const getNonNoiseSlotCount = (item) => {
  if (!item) return 0;
  const slots = getItemSlots(item);
  if (slots.length) return slots.filter(slot => !isNoiseItem(slot)).length;
  if (Array.isArray(item.slot_ids) && item.slot_ids.length) {
    return isNoiseItem(item) ? 0 : item.slot_ids.length;
  }
  return isNoiseItem(item) ? 0 : (item.id != null ? 1 : 0);
};

export const getDisplaySlotCount = (item, { includeNoise = false } = {}) => {
  if (!item) return 0;
  const slots = getItemSlots(item);
  if (slots.length) {
    return includeNoise ? slots.length : getNonNoiseSlotCount(item);
  }
  if (Array.isArray(item.slot_ids) && item.slot_ids.length) {
    return includeNoise ? item.slot_ids.length : getNonNoiseSlotCount(item);
  }
  return isNoiseItem(item) && !includeNoise ? 0 : (item.id != null ? 1 : 0);
};

export const isNoiseEntry = (entry) => {
  const slots = getItemSlots(entry);
  // Noise belongs to TimeSlot. The parent flag may be stale after an
  // individual child is restored, so it is only a fallback without slots.
  if (slots.length) return slots.every(isNoiseItem);
  return isNoiseItem(entry);
};

export const hasFocusableSlot = (entry) => {
  if (!entry) return false;
  const slots = getItemSlots(entry);
  if (!slots.length) return !isNoiseItem(entry);
  return slots.some(slot => !isNoiseItem(slot));
};

export const getVisibleSuggestedEntries = (entries = [], includeNoise = false) =>
  includeNoise
    ? entries
    : entries.filter(entry => hasFocusableSlot(entry) && !isNoiseEntry(entry));

export const getNoiseOnlySuggestedEntries = (entries = []) =>
  // MIRA-4326: Other is classified by the whole work item. A mixed entry stays in
  // Focused; its noise slots must not create a second copy in Other.
  entries.filter(isNoiseEntry);

export const countSuggestedEntryGroups = (entries = [], noiseMode = "focused") => {
  const groups = new Set();
  let ungroupedCount = 0;

  (Array.isArray(entries) ? entries : []).forEach(entry => {
    if (noiseMode === "focused" && (!hasFocusableSlot(entry) || isNoiseEntry(entry))) return;
    if (noiseMode === "all" && !isNoiseEntry(entry)) return;

    const groupId = entry.group_id;
    if (groupId !== undefined && groupId !== null && String(groupId).trim() !== "") {
      groups.add(String(groupId));
    } else {
      ungroupedCount += 1;
    }
  });

  return groups.size + ungroupedCount;
};

export const getFocusableSlots = (slots) => (Array.isArray(slots) ? slots.filter(slot => !isNoiseItem(slot)) : []);

export const getVisibleEntrySlots = (entry, showNoise = false) => {
  const slots = getItemSlots(entry);
  if (!slots.length) return [];
  return showNoise ? slots : getFocusableSlots(slots);
};

/**
 * @param {Record<string, any> | null | undefined} entry
 * @param {Record<string, any> | null | undefined} durationSlot
 */
export const hasUnavailableEntryDuration = (entry, durationSlot = null) =>
  entry?.duration == null ||
  entry?.stop == null ||
  Boolean(durationSlot && !durationSlot.stop_display);

/** @param {Record<string, any> | null | undefined} slot */
export const hasUnavailableSlotDuration = (slot) => !slot?.stop_display;

export const getNoiseSlotIds = (itemOrItems) => {
  const items = Array.isArray(itemOrItems) ? itemOrItems : [itemOrItems];
  const ids = [];

  items.forEach(item => {
    if (!item) return;
    const slots = getItemSlots(item);
    if (slots.length) {
      slots.forEach(slot => {
        if (slot?.id != null && isNoiseItem(slot)) ids.push(slot.id);
      });
      return;
    }
    if (Array.isArray(item.slot_ids) && item.slot_ids.length && isNoiseItem(item)) {
      item.slot_ids.forEach(id => {
        if (id != null) ids.push(id);
      });
      return;
    }
    if (item.id != null && isNoiseItem(item)) ids.push(item.id);
  });

  return ids;
};

export const getNoiseSlotIdsForNonNoiseTimeEntries = (itemOrItems) => {
  const items = Array.isArray(itemOrItems) ? itemOrItems : [itemOrItems];
  const ids = [];

  items.forEach(item => {
    if (!item) return;

    const slots = getItemSlots(item);
    if (slots.length) {
      if (isNoiseEntry(item)) return;
      slots.forEach(slot => {
        if (slot?.id != null && isNoiseItem(slot)) ids.push(slot.id);
      });
      return;
    }

    if (Array.isArray(item.slot_ids) && item.slot_ids.length) {
      return;
    }

    const relatedTimeEntry = item.relateTimeEntry || item.timeEntry || item.time_entry || item.timeentry_data;
    if (relatedTimeEntry && isNoiseEntry(relatedTimeEntry)) return;
    const looksLikeTimeSlot = item.timeentry != null || item.timeentry_id != null || item.time_entry_id != null || item.object_type === 'TimeSlot';
    if (item.id != null && isNoiseItem(item) && (relatedTimeEntry || looksLikeTimeSlot)) ids.push(item.id);
  });

  return Array.from(new Set(ids));
};

export const collectTimeSlotIds = (itemOrItems) => {
  const items = Array.isArray(itemOrItems) ? itemOrItems : [itemOrItems];
  const ids = [];

  items.forEach(item => {
    if (!item) return;

    const slots = getItemSlots(item);
    if (slots.length) {
      slots.forEach(slot => {
        if (slot?.id != null) ids.push(slot.id);
      });
      return;
    }

    if (Array.isArray(item.slot_ids) && item.slot_ids.length) {
      item.slot_ids.forEach(id => {
        if (id != null) ids.push(id);
      });
      return;
    }

    if (item.id != null) ids.push(item.id);
  });

  return Array.from(new Set(ids));
};

export const getSlotDurationSeconds = (slot) => {
  if (!slot) return 0;

  // Raw activity payloads carry applied duration in seconds. Prefer that (or
  // the captured time range) before the normalized timeline `duration`, which
  // is expressed in hours. Reading a raw value such as 141 as hours produced
  // the 141h totals seen in Time View and the Time Entry activity panel.
  const appliedDuration = Number(slot.min_working_applied_duration);
  if (Number.isFinite(appliedDuration) && appliedDuration > 0) {
    return appliedDuration;
  }

  const start = new Date(slot.start);
  const stop = new Date(slot.stop);
  const diffSeconds = (stop.getTime() - start.getTime()) / 1000;
  if (Number.isFinite(diffSeconds) && diffSeconds > 0) {
    return diffSeconds;
  }

  const durationHours = Number(slot.duration);
  if (Number.isFinite(durationHours) && durationHours > 0) {
    return Math.round(durationHours * 3600);
  }

  return 0;
};

export const sumEntrySlotDuration = (entry, { excludeNoise = false, preferEntryDuration = false } = {}) => {
  if (!entry) return 0;

  const slots = getItemSlots(entry);
  if (slots.length) {
    const hasExcludedNoiseSlot = excludeNoise && slots.some(isNoiseItem);
    const persistedDuration = Number(entry.duration);

    if (preferEntryDuration && Number.isFinite(persistedDuration) && persistedDuration >= 0) {
      return persistedDuration;
    }

    // The time-entry duration is independently editable and is the source of
    // truth when every slot participates in the total. Recalculate from slots
    // only when ignored/noise slots must be excluded, or when the API did not
    // provide a usable entry duration.
    if (!hasExcludedNoiseSlot && Number.isFinite(persistedDuration) && persistedDuration >= 0) {
      return persistedDuration;
    }

    return slots.reduce((sum, slot) => {
      if (excludeNoise && isNoiseItem(slot)) return sum;
      return sum + getSlotDurationSeconds(slot);
    }, 0);
  }

  if (excludeNoise && isNoiseItem(entry)) return 0;

  const duration = Number(entry.duration);
  if (!Number.isFinite(duration) || duration <= 0) return 0;
  return duration;
};

export const sumNonNoiseEntryDuration = (entries = []) => {
  return (Array.isArray(entries) ? entries : []).reduce((sum, entry) => {
    if (!entry || isNoiseEntry(entry)) return sum;
    return sum + sumEntrySlotDuration(entry, { excludeNoise: true });
  }, 0);
};

// Matter View counts the groups shown in Suggested Entries and individual
// captured slots in Activities. All counts each group only once.
export const getMatterViewFilterCounts = (entries, slots, activeTab) => {
  if (activeTab === 'suggested_entries') {
    return {
      focused: countSuggestedEntryGroups(entries),
      others: countSuggestedEntryGroups(entries, 'all'),
      total: countSuggestedEntryGroups(entries, 'everything'),
    };
  }
  const captured = slots.filter(slot => !slot.is_open && !slot.filteredOut);
  return {
    focused: captured.filter(slot => !isNoiseItem(slot)).length,
    others: captured.filter(isNoiseItem).length,
    total: captured.length,
  };
};
